import React from 'react';
import { calculateTotalReceivables } from '../../utils/financialUtils';
import OverviewHeader from './overview/OverviewHeader';
import StatsGrid from './overview/StatsGrid';
import QuickActions from './overview/QuickActions';
import TeamGameManagement from './overview/TeamGameManagement';
import FinancialManagement from './overview/FinancialManagement';
import CommunicationManagement from './overview/CommunicationManagement';
import EventManagement from './overview/EventManagement';
import ClubSettingsManagement from './overview/ClubSettingsManagement';

const OverviewView = ({
  club,
  teams,
  tournaments,
  games,
  expenses,
  walletTransactions,
  events,
  copied,
  onCopyCode,
  onNavigate,
  onSendReminders,
  players = [],
  permissions = {
    canManageClub: true,
    canManageFinancials: true,
    canViewFinancials: true,
    canManageTeams: true,
    canManageEvents: true,
    isAdmin: true,
    isTreasurer: true,
    isCaptain: true,
    isAuditor: true
  }
}) => {
  // Create a set of active player IDs for filtering
  // STRICT FILTER: Only approved players
  const activePlayerIds = new Set(players.filter(p => p.clubStatus === 'approved').map(p => p.id));

  // Calculate Total Receivables using centralized logic with ROBUST ERROR HANDLING
  // Matches "Manage Payments" view logic
  const totalReceivables = React.useMemo(() => {
    try {
      // Validate inputs before calculation
      if (!players || !Array.isArray(players) || !expenses || !games || !walletTransactions) {
        console.warn('[OverviewDebug] Missing required data for receivables calculation:', {
          hasPlayers: !!players,
          hasExpenses: !!expenses,
          hasGames: !!games,
          hasTransactions: !!walletTransactions
        });
        return 0;
      }

      return calculateTotalReceivables(players, expenses, games, walletTransactions);
    } catch (error) {
      // Catch ANY error during calculation to prevent white page crash
      console.error('[OverviewDebug] CRITICAL ERROR calculating receivables:', error);
      console.error('[OverviewDebug] Error Stack:', error.stack);
      return 0; // Fallback to safe value so page still renders
    }
  }, [players, expenses, games, walletTransactions]);

  // DEBUG: Log calculation details
  // Only log if user has financial permissions to avoid console clutter/leak
  if (permissions.canViewFinancials) {
    console.log('📊 OverviewView Financial Calculation Total:', totalReceivables);
  }

  // Count upcoming events
  const upcomingEventsCount = (events || []).filter(e => {
    if (!e.date) return false;
    // Compare dates as strings (YYYY-MM-DD) to avoid timezone issues
    const eventDateStr = e.date;
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }); // YYYY-MM-DD in PST

    return eventDateStr >= todayStr;
  }).length;

  return (
    <div>
      {/* Club Info */}
      <OverviewHeader
        club={club}
        copied={copied}
        onCopyCode={onCopyCode}
      />

      {/* Stats Grid */}
      <StatsGrid
        teams={teams}
        tournaments={tournaments}
        games={games}
        upcomingEventsCount={upcomingEventsCount}
        totalReceivables={totalReceivables}
        onNavigate={onNavigate}
        permissions={permissions}
      />

      {/* Quick Actions */}
      <QuickActions
        onNavigate={onNavigate}
        permissions={permissions}
      />

      {/* Management Cards */}
      <div className="space-y-6">

        {/* Team & Game Management Section */}
        <TeamGameManagement
          onNavigate={onNavigate}
          permissions={permissions}
        />

        {/* Financial Management Section */}
        <FinancialManagement
          onNavigate={onNavigate}
          permissions={permissions}
        />

        {/* Communication Section */}
        <CommunicationManagement
          onNavigate={onNavigate}
          onSendReminders={onSendReminders}
          permissions={permissions}
        />

        {/* Club Events Section */}
        <EventManagement
          onNavigate={onNavigate}
          upcomingEventsCount={upcomingEventsCount}
          permissions={permissions}
        />

        {/* Club Settings Section */}
        <ClubSettingsManagement
          onNavigate={onNavigate}
          permissions={permissions}
        />
      </div>
    </div>
  );
};

export default OverviewView;